import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import * as _ from 'lodash';

import { SVG } from '../../helpers/svg.helper';
import { CuttingCustomHole } from '../../models/cutting/cutting-custom-hole.model';
import { CuttingFixedFastenersSet } from '../../models/cutting/cutting-fixed-fasteners-set.model';
import { AnglePosition } from '../../models/cutting/cutting-panel-outline.model';
import {
  AxisTextData,
  CutoutFragmentData,
  CutoutPathData,
  FastenersSetPathData,
  HolePathData,
  LineData,
  PathData,
  Point,
  Position,
  RectangleData,
  TextData,
} from '../../models/cutting/cutting-svg.model';
import { PanelBase } from '../../models/cutting/panel-base.model';
import { PostformingSideEnum } from '../../models/user/cutting-settings.model';

@Component({
  selector: 'csl-svg-workspace',
  templateUrl: './svg-workspace.component.html',
  styleUrls: ['./svg-workspace.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SvgWorkspaceComponent implements OnInit {
  svg: SVG = undefined;

  panel: PanelBase;

  _defaultSelectedSide: Position;

  @Input() svgMaxSize = 350;

  @Input() set p(val: PanelBase) {
    this.panel = _.cloneDeep(val);
  }

  @Input() selectableHoles = false;
  @Input() sideSelectMode = false;
  @Input() axis = false;
  @Input() axisBase: Point = { x: 0, y: 0 };
  @Input() highlightedHole: CuttingCustomHole[];
  @Input() highlightedFastenersSet: CuttingFixedFastenersSet;
  @Output() selectSideChange: EventEmitter<Position> = new EventEmitter();
  @Input() set defaultSelectedSide(val: Position) {
    this._defaultSelectedSide = val;
  }
  get defaultSelectedSide(): Position {
    return this._defaultSelectedSide;
  }

  @Input() basePointSelectMode = false;
  @Input() sideBasePoints: Position | undefined;

  @Input() postforming: PostformingSideEnum = PostformingSideEnum.None;

  @Output() selectBasePoint: EventEmitter<AnglePosition> = new EventEmitter();
  @Output() highlightedHoleChange: EventEmitter<CuttingCustomHole[]> = new EventEmitter();
  @Output() highlightedFastenersSetChange: EventEmitter<CuttingFixedFastenersSet> = new EventEmitter();

  @ViewChild('workspace', { static: true }) workspace: ElementRef;

  constructor() {}

  ngOnInit(): void {
    this.svg = this.createSvg();
  }

  createSvg(): SVG {
    const [xScale, yScale] = this.getPanelScale(this.panel);

    const svg: SVG = new SVG(this.svgMaxSize, xScale, yScale);

    this.workspace.nativeElement.appendChild(svg.element);

    return svg;
  }

  private getPanelScale(panel: PanelBase): [number, number] {
    // scale 1:1
    let yScale = 1;
    let xScale = 1;

    if (panel) {
      if (panel.height < panel.width) {
        xScale = panel.height / panel.width || 1;
      } else {
        yScale = panel.width / panel.height || 1;
      }
    }

    return [xScale, yScale];
  }

  draw(panel?: PanelBase): void {
    // if component don't inited
    if (!this.svg || (panel && (!panel?.width || !panel?.height))) {
      return;
    }

    if (panel && (panel.width != this.panel.width || panel.height != this.panel.height)) {
      this.panel.height = panel.height;
      this.panel.width = panel.width;

      const [xScale, yScale] = this.getPanelScale(this.panel);

      this.svg.resize(this.svgMaxSize, xScale, yScale);
    }

    if (panel && panel.panelOutline) {
      this.panel.panelOutline = _.cloneDeep(panel.panelOutline);
    }

    if (this.axis) {
      this.drawAxis();
    }

    this.drawGrooves();
    this.drawCutouts();
    this.drawHoles(this.highlightedHole);
    // this.drawCustomHoles(this.highlightedHole);
    this.drawFastenerSets(this.highlightedFastenersSet);
    this.drawInfoLines();
    this.drawPostforming();
    this.drawMainOutline();
    this.drawText();

    if (this.sideSelectMode) {
      this.drawSideSelectors();
    }

    if (this.basePointSelectMode) {
      this.drawBasePointSelectors();
    }
  }

  setBasePoint(point: AnglePosition): void {
    const baseId = 'base-point-selector-' + point;
    const basePoint: HTMLElement = this.workspace.nativeElement.querySelector(`#${baseId}`);

    if (basePoint) {
      const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: false,
      });

      basePoint.dispatchEvent(clickEvent);
    }
  }

  drawBasePointSelectors(): void {
    const selectorsData = this.svg.getBasePointSelectors(this.panel, this.sideBasePoints);

    selectorsData.forEach(item => {
      this.drawBasePointSelector(item.position as AnglePosition, { x: item.x, y: item.y });
    });
  }

  drawAxis(): void {
    let axisWrapper: Element = this.workspace.nativeElement.querySelector('#axis-wrapper');

    if (!axisWrapper) {
      axisWrapper = document.createElementNS(this.svg.svgNS, 'g');
      axisWrapper.setAttribute('id', 'axis-wrapper');

      this.svg.element.appendChild(axisWrapper);
    }

    // axis
    let axis: Element = this.workspace.nativeElement.querySelector('#axis-path');

    if (!axis) {
      axis = document.createElementNS(this.svg.svgNS, 'path');
      axis.setAttribute('class', 'axis');
      axis.setAttribute('id', 'axis-path');
      axisWrapper.appendChild(axis);
    }

    const axisData = this.svg.getXYAxis(this.axisBase, this.panel.height, this.panel.width);
    axis.setAttribute('d', this.svg.interpolateLinear(axisData));

    // text
    const texts: AxisTextData[] = this.svg.getXYAxisText(this.panel, this.axisBase);

    texts.forEach(t => {
      let text: Element = this.workspace.nativeElement.querySelector(`#${t.id.toString()}`);

      if (!text) {
        text = document.createElementNS(this.svg.svgNS, 'text');
        text.setAttribute('id', t.id.toString());
        text.setAttribute('class', 'text axis-text');

        axisWrapper.appendChild(text);
      }

      text.setAttribute('x', t.x.toString());
      text.setAttribute('y', t.y.toString());
      text.textContent = t.text;
    });
  }

  drawText(): void {
    const texts: TextData[] = this.svg.getText(this.panel);

    texts.forEach(t => {
      let text: Element = this.workspace.nativeElement.querySelector(`#${t.position.toString()}`);

      if (!text) {
        text = document.createElementNS(this.svg.svgNS, 'text');
        text.setAttribute('id', t.position.toString());
        text.setAttribute('class', 'text');

        if (t.position === 'lc') {
          text.setAttribute('transform', `rotate(-90,${t.x},${t.y})`);
        }

        this.svg.element.appendChild(text);
      }

      text.setAttribute('x', t.x.toString());
      text.setAttribute('y', t.y.toString());
      text.textContent = t.text;
    });
  }

  drawMainOutline(): void {
    let outlinePath: Element = this.workspace.nativeElement.querySelector('#main-outline-path');

    if (!outlinePath) {
      outlinePath = document.createElementNS(this.svg.svgNS, 'path');
      outlinePath.setAttribute('class', 'main-outline');
      outlinePath.setAttribute('id', 'main-outline-path');
      outlinePath.setAttribute(
        'style',
        `fill: none;
          stroke: black;
          stroke-width: 2;
          stroke-linecap: round;
          stroke-linejoin: round;`
      );
      this.svg.element.appendChild(outlinePath);
    }

    const pathData: PathData[] = this.svg.getMainOutlinePathData(this.panel);
    outlinePath.setAttribute('d', this.svg.interpolateLinear(pathData));
  }

  drawPostforming(): void {
    if (this.postforming === PostformingSideEnum.None) {
      return;
    }

    let postformingLine: Element = this.workspace.nativeElement.querySelector('#postforming');

    if (!postformingLine) {
      postformingLine = document.createElementNS(this.svg.svgNS, 'line');
      postformingLine.setAttribute(
        'class',
        (this.postforming === PostformingSideEnum.TopAndBottom ? 'top' : 'bottom') + '-postforming'
      );
      postformingLine.setAttribute('id', 'postforming');
      this.svg.element.appendChild(postformingLine);
    }

    const lineData: LineData = this.svg.getPostformingLineData(this.panel, this.postforming);
    postformingLine.setAttribute('x1', lineData.p1.x.toString());
    postformingLine.setAttribute('y1', lineData.p1.y.toString());
    postformingLine.setAttribute('x2', lineData.p2.x.toString());
    postformingLine.setAttribute('y2', lineData.p2.y.toString());
  }

  drawInfoLines(): void {
    const infoLines: PathData[] = this.svg.getInfoLines(this.panel);

    infoLines.forEach(l => {
      let line: Element = this.workspace.nativeElement.querySelector(`#${l.mode}`);
      if (!line) {
        line = document.createElementNS(this.svg.svgNS, 'polyline');
        line.setAttribute('class', 'info-line');
        line.setAttribute('id', l.mode);
        this.svg.element.appendChild(line);
      }
      line.setAttribute('points', l.data.join(' '));
    });
  }

  drawGrooves(): void {
    const grooves: RectangleData[] = this.svg.getGrooves(this.panel);

    grooves.forEach(g => {
      let groove: Element = this.workspace.nativeElement.querySelector(`#${g.id}`);

      if (!groove) {
        groove = document.createElementNS(this.svg.svgNS, 'rect');

        groove.setAttribute('id', g.id);
        this.svg.element.appendChild(groove);
      }

      groove.setAttribute('class', g.class + ' groove');
      groove.setAttribute('x', g.x.toString());
      groove.setAttribute('y', g.y.toString());
      groove.setAttribute('height', g.height.toString());
      groove.setAttribute('width', g.width.toString());
    });
  }

  drawCutouts(): void {
    this.svg.getCutouts(this.panel).then(cutouts => {
      this.drawCutout(cutouts);
    });
  }

  drawHoles(highlightedHole: CuttingCustomHole[] | undefined): void {
    const holes: HolePathData[] = this.svg.getCusttomHoles(this.panel, highlightedHole);

    this.drawHole(holes);
  }

  drawFastenerSets(highlightedSet?: CuttingFixedFastenersSet | undefined): void {
    const sets: FastenersSetPathData[] = this.svg.getFixedFastenerSets(this.panel, highlightedSet);

    this.drawFastenersSet(sets);
  }

  private drawFastenersSet(sets: FastenersSetPathData[]): void {
    const setsElements: Element[] = this.workspace.nativeElement.querySelectorAll('.fastener-set');
    setsElements.forEach(s => {
      s.parentNode.removeChild(s);
    });

    sets.forEach(s => {
      const setEl = document.createElementNS(this.svg.svgNS, 'g');

      setEl.setAttribute('class', s.class + ' fastener-set' + (this.selectableHoles ? ' selectable' : ''));

      // add holes
      s.holes.forEach(h => {
        const holeEl = document.createElementNS(this.svg.svgNS, 'path');

        holeEl.setAttribute('class', h.class + ' custom-hole' + (this.selectableHoles ? ' selectable' : ''));
        holeEl.setAttribute('d', this.svg.interpolateLinear(h.data));

        if (this.selectableHoles) {
          (holeEl as SVGPathElement).onclick = (ev: MouseEvent) => {
            const target = ev.target as SVGPathElement;

            if (target.parentElement.classList.contains('selected')) {
              this.highlightedFastenersSetChange.emit(undefined);
            } else {
              target.parentElement.classList.add('selected');
              this.highlightedFastenersSetChange.emit(s.fastenersSet);
            }
            return true;
          };
        }

        setEl.appendChild(holeEl);
      });

      this.svg.element.appendChild(setEl);
    });
  }

  private drawHole(holes: HolePathData[]): void {
    const holeElements: Element[] = this.workspace.nativeElement.querySelectorAll(`.custom-hole`);
    holeElements.forEach(he => {
      he.parentNode.removeChild(he);
    });

    holes.forEach(h => {
      const holeEl = document.createElementNS(this.svg.svgNS, 'path');

      holeEl.setAttribute('class', h.class + ' custom-hole' + (this.selectableHoles ? ' selectable' : ''));
      holeEl.setAttribute('d', this.svg.interpolateLinear(h.data));

      if (this.selectableHoles) {
        (holeEl as SVGPathElement).onclick = (ev: MouseEvent) => {
          const target = ev.target as SVGPathElement;

          if (target.classList.contains('selected')) {
            this.highlightedHoleChange.emit(undefined);
          } else {
            target.classList.add('selected');
            this.highlightedHoleChange.emit([h.hole]);
          }
          return true;
        };
      }

      this.svg.element.appendChild(holeEl);
    });
  }

  private drawCutout(cutout: Array<CutoutPathData | CutoutFragmentData>): void {
    const cutoutElements: Element[] = this.workspace.nativeElement.querySelectorAll(`.cutout`);
    cutoutElements.forEach(c => {
      c.parentNode.removeChild(c);
    });

    cutout.forEach(c => {
      const groupEl = document.createElementNS(this.svg.svgNS, 'g');

      groupEl.setAttribute('class', 'cutout');

      if (this.isFragmentCutout(c)) {
        groupEl.innerHTML = c.cutout;
        groupEl.setAttribute('transform', `rotate(${c.rotateDeg}, ${c.base.cx}, ${c.base.cy})`);
        const cutoutEl = groupEl.getElementsByTagName('svg')[0];
        cutoutEl.setAttribute('x', `${c.x}px`);
        cutoutEl.setAttribute('y', `${c.y}px`);
        cutoutEl.setAttribute('height', `${c.width}px`);
        cutoutEl.setAttribute('width', `${c.height}px`);
        const elId = (cutoutEl.id = 'fragment-cutout-svg-' + Date.now());

        const style = document.createElement('style');
        style.type = 'text/css';

        style.innerHTML = `
            svg#${elId} * {
                stroke-width: 2px;
                vector-effect: non-scaling-stroke;
                stroke: black;
            }
        `;
        cutoutEl.appendChild(style);
      } else {
        const cutoutEl = document.createElementNS(this.svg.svgNS, 'path');
        cutoutEl.setAttribute('d', this.svg.interpolateLinear(c.cutout));
        groupEl.appendChild(cutoutEl);
      }

      const baseEl = document.createElementNS(this.svg.svgNS, 'circle');
      baseEl.setAttribute('cx', c.base.cx.toString());
      baseEl.setAttribute('cy', c.base.cy.toString());
      baseEl.setAttribute('r', c.base.r.toString());
      groupEl.appendChild(baseEl);

      this.svg.element.appendChild(groupEl);
    });
  }

  drawSideSelectors(): void {
    const selectorsData = this.svg.getSideSelectors(this.panel);

    this.drawSideSelector('tc', selectorsData[0]);
    this.drawSideSelector('rc', selectorsData[1]);
    this.drawSideSelector('bc', selectorsData[2]);
    this.drawSideSelector('lc', selectorsData[3]);
  }

  private drawBasePointSelector(position: AnglePosition, centerPoint: Point): void {
    const baseId = 'base-point-selector-' + position;

    let basePoint: Element = this.workspace.nativeElement.querySelector(`#${baseId}`);

    if (!basePoint) {
      basePoint = document.createElementNS(this.svg.svgNS, 'image');
      basePoint.setAttribute('width', '45');
      basePoint.setAttribute('height', '45');
      basePoint.setAttribute('id', baseId);
      basePoint.setAttribute('class', 'base-point-selector');
      basePoint.setAttributeNS(
        'http://www.w3.org/1999/xlink',
        'href',
        'assets/images/outline-editor/basepoint_selector.svg'
      );

      (basePoint as SVGImageElement).onclick = (ev: MouseEvent) => {
        const target = ev.target as SVGPathElement;
        const points: Element[] = Array.from(this.workspace.nativeElement.querySelectorAll('.base-point-selector'));
        points.forEach(p => {
          if (p.id !== target.id) {
            p.classList.remove('selected-base-point');
            p.setAttributeNS(
              'http://www.w3.org/1999/xlink',
              'href',
              'assets/images/outline-editor/basepoint_selector.svg'
            );
          }
        });

        this.toggleXbaseArrow(position, centerPoint);

        target.classList.toggle('selected-base-point');
        basePoint.setAttributeNS(
          'http://www.w3.org/1999/xlink',
          'href',
          `assets/images/outline-editor/basepoint-selector-active.svg`
        );

        this.selectBasePoint.emit(position);
        return true;
      };

      this.svg.element.appendChild(basePoint);
    }

    basePoint.setAttribute('x', (centerPoint.x - 22.5).toString());
    basePoint.setAttribute('y', (centerPoint.y - 22.5).toString());
  }

  private toggleXbaseArrow(position: AnglePosition, centerPoint: Point): void {
    const xBaseId = 'base-point-selector-' + position + '-x';

    let basePointXArrow: Element = this.workspace.nativeElement.querySelector(`#${xBaseId}`);

    if (!basePointXArrow && this.sideBasePoints === undefined) {
      const xArrows: Element[] = Array.from(this.workspace.nativeElement.querySelectorAll('.selected-base-point-x'));
      xArrows.forEach(a => {
        a.parentNode.removeChild(a);
      });

      basePointXArrow = document.createElementNS(this.svg.svgNS, 'image');
      basePointXArrow.setAttribute('width', '45');
      basePointXArrow.setAttribute('height', '45');
      basePointXArrow.setAttribute('id', xBaseId);
      basePointXArrow.setAttribute('class', 'base-point-selector selected-base-point-x');
      basePointXArrow.setAttributeNS(
        'http://www.w3.org/1999/xlink',
        'href',
        'assets/images/outline-editor/basepoint-selector-active.svg'
      );

      this.svg.element.appendChild(basePointXArrow);
    } else if (basePointXArrow && this.sideBasePoints === undefined) {
      basePointXArrow.parentNode.removeChild(basePointXArrow);
    }

    if (basePointXArrow) {
      basePointXArrow.setAttribute('x', (centerPoint.x - 22.5).toString());
      basePointXArrow.setAttribute('y', (centerPoint.y - 22.5).toString());
    }
  }

  private getXBasePointMarkerRotate(point: Position): number {
    if (point === 'bl' || point === 'tl') {
      return 0;
    } else {
      return 180;
    }
  }

  private getBasePointMarkerRotate(side: Position | undefined, point: Position): number {
    if (side === undefined) {
      if (point === 'bl' || point === 'br') {
        return -90;
      } else {
        return 90;
      }
    }

    // right
    if ((side === 'tc' || side === 'bc') && (point === 'tl' || point === 'bl')) {
      return 0;
    }

    // left
    if ((side === 'tc' || side === 'bc') && (point === 'tr' || point === 'br')) {
      return 180;
    }

    // down
    if ((side === 'lc' || side === 'rc') && (point === 'tr' || point === 'tl')) {
      return 90;
    }

    // up
    if ((side === 'lc' || side === 'rc') && (point === 'br' || point === 'bl')) {
      return -90;
    }

    return 0;
  }

  private drawSideSelector(position: Position, data: PathData[]): void {
    const sideId = 'side-selector-' + position;

    let sidePath: Element = this.workspace.nativeElement.querySelector(`#${sideId}`);

    if (!sidePath) {
      sidePath = document.createElementNS(this.svg.svgNS, 'path');
      sidePath.setAttribute('class', 'side-selector');
      sidePath.setAttribute('id', sideId);

      if (this.defaultSelectedSide === position) {
        sidePath.classList.add('selected-side');
      }

      (sidePath as SVGPathElement).onclick = (ev: MouseEvent) => {
        const target = ev.target as SVGPathElement;
        const sides: Element[] = Array.from(this.workspace.nativeElement.querySelectorAll('.side-selector'));
        sides.forEach(s => {
          if (s.id !== target.id) {
            s.classList.remove('selected-side');
          }
        });

        target.classList.add('selected-side');
        this.selectSideChange.emit(position);
        return true;
      };

      this.svg.element.appendChild(sidePath);
    }

    sidePath.setAttribute('d', this.svg.interpolateLinear(data));
  }

  private isFragmentCutout(cutoutData: CutoutPathData | CutoutFragmentData): cutoutData is CutoutFragmentData {
    return typeof cutoutData.cutout === 'string';
  }
}
